/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { EmailRegex, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { CheckoutModule, ErrorLocation } from '@msdyn365-commerce/global-state';
import classnames from 'classnames';
import { action, computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { focusOnCheckoutError } from '@msdyn365-commerce-modules/checkout';
import { ErrorComponent } from '@msdyn365-commerce-modules/checkout';

import { IDlvryCheckoutV2GuestProfileData } from './dlvry-checkout-v2-guest-profile.data';
import { IDlvryCheckoutV2GuestProfileProps } from './dlvry-checkout-v2-guest-profile.props.autogenerated';
import getAddContactInfo, { IAddContactInfo } from './components/get-add-contact-info';
import getShowContactInfo, { IShowContactInfo } from './components/get-show-contact-info';
import { CheckoutAppliedStateInput } from '../dlvry-checkout-v2/actions/checkout-applied-state';
import { IModuleStatePropsExt } from '../../shared-utils/types';

export * from './components/get-add-contact-info';
export * from './components/get-show-contact-info';

interface ICheckoutGuestProfileState {
    email: string;
    customerRequisition: string;
    customerRequisitionLocked: boolean;
    customerReference: string;
    customerReferenceLocked: boolean;
    customerMessage: string;
    hasCheckoutError?: boolean;
    errorMessage?: string;
}

export interface ICheckoutGuestProfileModuleProps extends IDlvryCheckoutV2GuestProfileProps<IDlvryCheckoutV2GuestProfileData>, IModuleStatePropsExt {}

export interface ICheckoutGuestProfileViewProps extends IDlvryCheckoutV2GuestProfileProps<IDlvryCheckoutV2GuestProfileData>, ICheckoutGuestProfileState {
    baseClassName: string;
    moduleProps: IModuleProps;
    showContactInfo?: IShowContactInfo;
    addContactInfo?: IAddContactInfo;
    alert?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;

    onChange?(email: string): void;
    onEdit?(): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 *
 * CheckoutGuestProfile component.
 * @extends {React.PureComponent<IDlvryCheckoutV2GuestProfileProps<IDlvryCheckoutV2GuestProfileData>, ICheckoutGuestProfileState>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutGuestProfile extends React.Component<ICheckoutGuestProfileModuleProps, ICheckoutGuestProfileState> {
    public state: ICheckoutGuestProfileState = {
        email:
            (this.props.context &&
                this.props.context.request &&
                this.props.context.request.user &&
                this.props.context.request.user.isAuthenticated &&
                this.props.context.request.user.emailAddress) ||
            '',
        customerRequisition: '',
        customerRequisitionLocked: false,
        customerReference: '',
        customerReferenceLocked: false,
        customerMessage: ''
    };

    private readonly CustomerRequisition = 'CUSTOMER_REQUISITION';
    private readonly CustomerReference = 'CUSTOMER_REFERENCE';
    private readonly CustomerMessage = 'CUSTOMER_MESSAGE';

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef();
    private readonly customerReferenceInputRef: React.RefObject<HTMLInputElement> = React.createRef();
    private readonly customerRequisitionInputRef: React.RefObject<HTMLInputElement> = React.createRef();
    private readonly customerMessageInputRef: React.RefObject<HTMLTextAreaElement> = React.createRef();

    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    @computed get hasEmailError(): boolean {
        return this.props.moduleState.hasError && !this.state.hasCheckoutError;
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                this.setState({
                    customerReference: await this.getExtensionProperty(this.CustomerReference),
                    customerRequisition: await this.getExtensionProperty(this.CustomerRequisition),
                    customerMessage: await this.getExtensionProperty(this.CustomerMessage)
                });

                await this.init();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            async isExpressCheckoutApplied => {
                const emailAddressFromExpressCheckout = this.props.data.checkout.result?.emailAddressFromExpressCheckout ?? '';
                const isEmailFromExpressCheckoutDifferent =
                    !StringExtensions.isNullOrEmpty(emailAddressFromExpressCheckout) &&
                    emailAddressFromExpressCheckout !== this.state.email;

                if (
                    isExpressCheckoutApplied &&
                    (!this.props.data.checkout.result?.isExpressAddressApplied || isEmailFromExpressCheckoutDifferent)
                ) {
                    await this.submitEmail(emailAddressFromExpressCheckout);
                }
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (checkoutError && checkoutError.errorLocation === ErrorLocation.CheckoutGuestProfile && checkoutError.errorMessage) {
                        this._setErrorMessage(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CheckoutGuestProfile) {
                        void focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutGuestProfileModuleProps, nextState: ICheckoutGuestProfileState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const {
            config: { className },
            resources,
            moduleState: { isReady, hasExternalSubmitGroup }
        } = this.props;
        const { email, hasCheckoutError, errorMessage } = this.state;
        const baseClassName = 'ms-checkout-guest-profile';

        const viewProps: ICheckoutGuestProfileViewProps = {
            ...this.props,
            ...this.state,
            baseClassName,
            checkoutErrorRef: this.checkoutErrorRef,
            onChange: this.onChange,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,

            moduleProps: {
                moduleProps: this.props,
                className: classnames(baseClassName, className, isReady ? 'show' : 'add')
            },
            alert: hasCheckoutError && errorMessage && (
                <ErrorComponent {...{ title: resources.errorMessageTitle, message: errorMessage }} />
            ),
            showContactInfo: isReady
                ? getShowContactInfo({
                    canEdit: !hasExternalSubmitGroup,
                    email,
                    customerRequisition: this.state.customerRequisition,
                    customerRequisitionLocked: this.state.customerRequisitionLocked,
                    customerReference: this.state.customerReference,
                    customerReferenceLocked: this.state.customerReferenceLocked,
                    customerMessage: this.state.customerMessage,
                    resources,
                    onEdit: this.onEdit
                  })
                : undefined,
            addContactInfo: !isReady
                ? getAddContactInfo({
                      baseClassName,
                      email: this.state.email,
                      customerReference: this.state.customerReference,
                      customerReferenceLocked: this.state.customerReferenceLocked,
                      customerRequisition: this.state.customerRequisition,
                      customerRequisitionLocked: this.state.customerRequisitionLocked,
                      customerMessage: this.state.customerMessage,
                      hasError: this.hasEmailError,
                      resources,
                      inputRef: this.inputRef,
                      customerReferenceInputRef: this.customerReferenceInputRef,
                      customerRequisitionInputRef: this.customerRequisitionInputRef,
                      customerMessageInputRef: this.customerMessageInputRef,
                      onChange: this.onChange,
                      onChangeCustomerRequisition: this.onChangeCustomerRequisition,
                      onChangeCustomerReference: this.onChangeCustomerReference,
                      onChangeCustomerMessage: this.onChangeCustomerMessage,
                      canSubmit: !hasExternalSubmitGroup,
                      onSubmit: this.onSubmit,
                      canCancel: !hasExternalSubmitGroup && !!email,
                      onCancel: this.onCancel
                  })
                : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly init = async (): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;
        const hasInvoiceLine = checkoutState?.checkoutCart.hasInvoiceLine;
        const email = this.getInitialEmail();

        await this.loadFixedReferenceAndRequisition();

        const isEmailValid = this.isEmailValid(email);
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: hasInvoiceLine ? 'disabled' : email && isEmailValid ? 'ready' : 'updating',
            hasError: !!email && !isEmailValid,
            wide: true
        });

        if (email) {
            await this.updateGuestProfile(email);
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: true });
        } else {
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: false });
        }

        if (email || this.state.email) {
            await this.onSubmit();
        }
    };

    private readonly onChange = (rowEmail: string): void => {
        const email = (rowEmail || '').replace(new RegExp('[<>]', 'gi'), '');
        this.setState({ email });
        if (this.props.moduleState.hasError) {
            // Clear error during user updating the email
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
        }
    };

    private readonly onChangeCustomerRequisition = (customerRequisition: string): void => {
        this.setState({ customerRequisition });
    };

    private readonly onChangeCustomerReference = (customerReference: string): void => {
        this.setState({ customerReference });
    };

    private readonly onChangeCustomerMessage = (customerMessage: string): void => {
        this.setState({ customerMessage });
    };

    private readonly setErrorMsgOboUserEmailEmpty = (): string => {
        // Clear prior error
        this.props.moduleState.setHasError(false);
        this.setState({
            hasCheckoutError: false
        });
        return '';
    };

    private readonly isEmailValid = (email: string): boolean => {
        return EmailRegex.defaultRegex.test(email);
    };

    private readonly getExtensionProperty = async (propertyName: string): Promise<string> => {
        const checkout = await this.props.data.checkout;

        if (
            this.props.context &&
            this.props.context.request &&
            this.props.context.request.user &&
            this.props.context.request.user.isAuthenticated &&
            checkout
        ) {
            const extensionProperty = checkout.checkoutCart.cart.ExtensionProperties?.find(property => {
                return property.Key === propertyName;
            });
            if (extensionProperty && extensionProperty.Value?.StringValue) {
                return extensionProperty.Value.StringValue;
            }
        }
        return '';
    };

    private readonly getInitialEmail = (): string => {
        const checkoutState = this.props.data.checkout.result;
        const customerAccountInformation = this.props.data.accountInformation?.result;
        const emailOnCart = checkoutState?.checkoutCart.cart.ReceiptEmail;
        const hasInvoiceLine = checkoutState?.checkoutCart.hasInvoiceLine;

        let updateEmail: string = '';

        // if obo enabled and profile selected, we need to send the order status to obo user not store manager.
        if (Msdyn365.isOboRequest(this.props.context.request)) {
            updateEmail =
                customerAccountInformation && customerAccountInformation.Email !== undefined
                    ? customerAccountInformation.Email
                    : this.setErrorMsgOboUserEmailEmpty();
        } else {
            updateEmail = this.state.email;
        }

        const email = hasInvoiceLine
            ? this.props.context.request.user.emailAddress ?? ''
            : emailOnCart ??
              (checkoutState?.isExpressCheckoutApplied && !StringExtensions.isNullOrEmpty(checkoutState.emailAddressFromExpressCheckout)
                  ? checkoutState.emailAddressFromExpressCheckout
                  : undefined) ??
              updateEmail;

        return email;
    };

    private readonly loadFixedReferenceAndRequisition = async (): Promise<void> => {
        const org = await this.props.data.organizationInformation;
        const ref = org?.ExtensionProperties?.find(x => x.Key === 'FIXED_REF')?.Value?.StringValue;
        const req = org?.ExtensionProperties?.find(x => x.Key === 'FIXED_REQ')?.Value?.StringValue;

        this.setState({
            customerReference: ref ?? this.state.customerReference,
            customerReferenceLocked: Boolean(ref),
            customerRequisition: req ?? this.state.customerRequisition,
            customerRequisitionLocked: Boolean(req)
        });
    };

    private readonly updateGuestProfile = async (newGuestCheckoutEmail: string): Promise<void> => {
        if (this.props.data.checkout.result) {
            // await this.props.data.checkout.result.updateGuestCheckoutEmail({ newGuestCheckoutEmail });

            const newApplied = { ...await this.props.data.checkoutAppliedState };
            newApplied.applied.customerInformation = true;
            newApplied.newCartObject.Comment = await this.getExtensionProperty(this.CustomerReference);
            newApplied.newCartObject.ReceiptEmail = newGuestCheckoutEmail;
            newApplied.newCartObject.ExtensionProperties = [
                { Key: this.CustomerRequisition, Value: { StringValue: await this.getExtensionProperty(this.CustomerRequisition) } },
                { Key: this.CustomerReference, Value: { StringValue: await this.getExtensionProperty(this.CustomerReference) } },
                { Key: this.CustomerMessage, Value: { StringValue: await this.getExtensionProperty(this.CustomerMessage) } }
            ];
            this.props.context.actionContext.update(new CheckoutAppliedStateInput(), newApplied);

            this.setState({ email: newGuestCheckoutEmail });
        }
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('Guest profile onEdit is called.');

        this.props.moduleState.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        this.props.telemetry.information('Guest profile onSubmit is called.');

        if (!this.props.data.checkout.result) {
            console.error('Could not find checkout cart to update guest checkout email.');
            return;
        }

        const { email, customerRequisition, customerReference, customerMessage } = this.state;

        const isValid = this.isEmailValid(email);
        if (!isValid) {
            this.props.moduleState.setHasError(true);

            const input = this.inputRef.current?.focus && (this.inputRef.current as HTMLElement);
            input?.focus();
            return;
        }

        try {
            /* const newCheckoutCart: Partial<Cart> = {
                Comment: customerReference,
                ReceiptEmail: email,
                ExtensionProperties: [
                    { Key: 'CUSTOMER_REQUISITION', Value: { StringValue: customerRequisition } },
                    { Key: 'CUSTOMER_REFERENCE', Value: { StringValue: customerReference } },
                    { Key: 'CUSTOMER_MESSAGE', Value: { StringValue: customerMessage } }
                ]
            };

            await this.props.data.checkout.result?.checkoutCart.updateCart({
                newCartObject: newCheckoutCart as Cart
            }); */
            // This will do an check internally to compare against email on cart and not send another
            // request if it's the same.
            // await this.props.data.checkout.result?.updateGuestCheckoutEmail({ newGuestCheckoutEmail: email });
            this.setState({ email });

            const newApplied = { ...await this.props.data.checkoutAppliedState };
            newApplied.applied.customerInformation = true;
            newApplied.newCartObject.Comment = customerReference;
            newApplied.newCartObject.ReceiptEmail = email;
            newApplied.newCartObject.ExtensionProperties = [
                { Key: this.CustomerRequisition, Value: { StringValue: customerRequisition } },
                { Key: this.CustomerReference, Value: { StringValue: customerReference } },
                { Key: this.CustomerMessage, Value: { StringValue: customerMessage } }
            ];
            this.props.context.actionContext.update(new CheckoutAppliedStateInput(), newApplied);

            this.props.moduleState.onReady();
        } catch (error) {
            alert('Error submitting ref and/or req to checkout cart.');
            console.error('Error submitting ref and/or req to checkout cart.');
            console.error(error);
            this.props.moduleState.setHasError(true);
        }
    };

    /**
     * Submit the email address.
     * @param email - The email to be submitted.
     *
     */
    private readonly submitEmail = async (email: string): Promise<void> => {
        const isValid = this.isEmailValid(email);
        if (!isValid) {
            this.props.moduleState.setHasError(true);

            const input = this.inputRef.current?.focus && (this.inputRef.current as HTMLElement);
            input?.focus();
        } else {
            await this.updateGuestProfile(email);
            this.props.moduleState.onReady();
        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('Guest profile onCancel is called.');

        // Reset to the saved value
        if (!this.props.data.checkout.result) {
            return;
        }

        const newCartObject = this.props.data.checkoutAppliedState.result?.newCartObject;
        const email = newCartObject?.ReceiptEmail ?? this.getInitialEmail();
        const customerReference = newCartObject?.ExtensionProperties?.find(x => x.Key === this.CustomerReference)?.Value?.StringValue ?? '';
        const customerRequisition = newCartObject?.ExtensionProperties?.find(x => x.Key === this.CustomerRequisition)?.Value?.StringValue ?? '';
        const customerMessage = newCartObject?.ExtensionProperties?.find(x => x.Key === this.CustomerMessage)?.Value?.StringValue ?? '';

        if (!email) {
            // It has no saved email, set to status updating and request user to fill the email form
            this.props.moduleState.onUpdating();
        } else {
            // It has saved email, set to status ready
            this.setState({
                email,
                customerReference,
                customerRequisition,
                customerMessage
            });
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
            this.props.moduleState.onReady();
        }
    };

    @action
    private readonly _setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.setState({
            hasCheckoutError: true,
            errorMessage
        });
    };
}

export default CheckoutGuestProfile;
